import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import { zipSync, strToU8 } from 'fflate'
import { getTorisageiraiXmlTemplate, getKouseiXmlTemplate } from '@/components/logics/EgovXmlTemplates'
import { formatDateTime } from '@/components/logics/dateUtil'

export const useEgovListStore = defineStore('egovList', {
  state: () => ({
    applications: [],
    tableList: [],
    formData: null,
    formInit: {
      id: null,
      rowNumber: 0,
      arriveId: '',
      corporationName: '',
      applicantName: '',
      procName: '',
      procId: '',
      submissionDestination: '',
      status: '',
      confirmTime: '',
      sendDate: '',
      applyType: '',
      ministryName: '',
      userId: '',
      userName: '',
      appliedAccount: '',
      tokenSub: '',
      tokenEmail: '',
      createdAt: '',
      updatedAt: '',
      docCount: 0,
      noticeCount: 0,
    },
    searchList: new Set(['到達', '到達（取下げ処理中）', '審査中', '審査完了']),
    offset: 0,
    limit: 50,
    totalCount: 0,
    loading: false,
    procList: [],
    noticeDetail: null, // 選択された通知の詳細情報
    officialDocumentDetail: null, // 選択された公文書の詳細情報
    officialDocumentList: [], // 公文書一覧
    signatureVerificationResult: null, // 署名検証の結果
  }),

  actions: {
    // テーブル定義の初期化
    initTableList() {
      this.tableList = [
        {
          label: 'No',
          key: 'rowNumber',
          data: (item, value) => value,
        },
        {
          label: '到達番号',
          key: 'arriveId',
          data: (item, value) => value,
        },
        {
          label: '到達日時',
          key: 'arriveDate',
          data: (item, value) => value.replace(/-/g, '/'),
        },
        {
          label: 'ステータス',
          key: 'status',
          data: (item, value) => value,
          tag: {
            到達: 'reached',
            '到達（取下げ処理中）': 'reached-withdrawn',
          },
          tag_css: 'wide',
        },
        // {
        //   label: '通知件数',
        //   key: 'noticeCount',
        //   data: (item, value) => value,
        // },
        // {
        //   label: '公文書件数',
        //   key: 'docCount',
        //   data: (item, value) => value,
        // },
        {
          label: '法人名',
          key: 'corporationName',
          data: (item, value) => value,
        },
        {
          label: '申請者名',
          key: 'applicantName',
          data: (item, value) => value,
        },
        {
          label: 'ディーモ申請者',
          key: 'userName',
          data: (item, value) => value,
        },
        {
          label: '手続名',
          key: 'procName',
          data: (item, value) => value,
        },
      ]
    },

    // 申請リスト取得
    async getApplications() {
      try {
        this.loading = true
        const params = {
          limit: this.limit,
          offset: this.offset,
          order: 'desc',
        }

        const response = await apiService.get('api/egov/applications', params)

        // apiServiceが自動でsnake_case→camelCase変換してくれる
        this.applications = response.applications || []
        this.totalCount = response.totalCount || 0

        return true
      } catch (error) {
        return false
      } finally {
        this.loading = false
      }
    },

    // フォームデータの設定
    async setFormData(id) {
      if (id) {
        // 編集の場合 - applications配列から該当データを取得
        const application = this.getApplicationById(id)
        if (application) {
          this.formData = JSON.parse(JSON.stringify(application))
          // 日付フォーマット
          this.formData.statusCheckDate = formatDateTime(this.formData.statusCheckDate)
          this.formData.arriveDate = formatDateTime(this.formData.arriveDate)
          this.formData.createdAt = formatDateTime(this.formData.createdAt)
          this.formData.updatedAt = formatDateTime(this.formData.updatedAt)
          return
        }
      }
      // 新規作成の場合
      this.clearFormData()
    },

    // フォームデータをクリア
    clearFormData() {
      this.formData = JSON.parse(JSON.stringify(this.formInit))
    },

    // 取り下げ処理
    async withdrawApplication(id, requesterName, reason) {
      try {
        const application = this.getApplicationById(id)
        if (!application) {
          throw new Error('Application not found')
        }

        const fileName = application.arriveId + '.zip'
        const procInfo = {
          'e-Gov上での表示手続名（手続名称）': application.procName,
          申請種別: '取下げ依頼',
        }

        const zipData = this.getEgovWithdrawZip(application.procId, application.arriveId, requesterName, reason, procInfo)
        const encodedZipData = btoa(String.fromCharCode(...zipData))

        const data = {
          id: id,
          arriveId: application.arriveId,
          fileName: fileName,
          fileData: encodedZipData,
        }

        const response = await apiService.post('/api/egov/withdraw', data)

        if (response.success) {
          // 成功時はリストを再取得
          await this.getApplications()
          return { success: true, message: 'e-Gov申請を取り下げました' }
        } else {
          return { success: false, message: response.message || '取り下げに失敗しました' }
        }
      } catch (error) {
        return { success: false, message: '取り下げ処理中にエラーが発生しました' }
      }
    },

    // 最初の通知取得
    async getFirstEGovNotice(id) {
      try {
        // 指定されたIDの申請を取得
        const application = this.getApplicationById(id)
        if (!application || !application.noticeList?.length) {
          return { success: false, message: '通知が見つかりません' }
        }

        const noticeSubId = application.noticeList[0].noticeSubId

        // 通知詳細を取得
        return await this.getEGovNotice(id, noticeSubId)
      } catch (error) {
        return { success: false, message: '通知の取得に失敗しました' }
      }
    },

    // 公文書取得
    async getOfficialDocument(id, noticeSubId) {
      try {
        this.loading = true
        // 指定されたIDの申請を取得
        const application = this.getApplicationById(id)
        if (!application) {
          return { success: false, message: '申請が見つかりません' }
        }

        const data = {
          arrive_id: application.arriveId,
          notice_sub_id: noticeSubId,
        }

        const response = await apiService.post('/api/egov/official-document', data)

        if (response.success) {
          // 公文書詳細をセット
          this.officialDocumentDetail = response.data || null

          return {
            success: true,
            data: this.officialDocumentDetail,
            message: response.message || '公文書を取得しました',
          }
        } else {
          return { success: false, message: response.message || '公文書の取得に失敗しました' }
        }
      } catch (error) {
        return { success: false, message: '公文書の取得に失敗しました' }
      } finally {
        this.loading = false
      }
    },

    // 最初の公文書取得
    async getFirstEGovOfficial(id) {
      try {
        // 指定されたIDの申請を取得
        const application = this.getApplicationById(id)
        if (!application || !application.officialList?.length) {
          return { success: false, message: '公文書が見つかりません' }
        }

        const noticeSubId = application.officialList[0].noticeSubId

        // 公文書詳細を取得
        return await this.getOfficialDocument(id, noticeSubId)
      } catch (error) {
        return { success: false, message: '公文書の取得に失敗しました' }
      }
    },

    // 公文書取得完了
    async completeOfficialDocument(id, noticeSubId) {
      try {
        this.loading = true
        // 指定されたIDの申請を取得
        const application = this.getApplicationById(id)
        if (!application) {
          return { success: false, message: '申請が見つかりません' }
        }

        const data = {
          arrive_id: application.arriveId,
          notice_sub_id: noticeSubId,
        }

        const response = await apiService.post('/api/egov/official-document/complete', data)

        if (response.success) {
          return {
            success: true,
            data: response.data,
            message: response.message || '公文書取得完了の通知に成功しました',
          }
        } else {
          return { success: false, message: response.message || '公文書取得完了の通知に失敗しました' }
        }
      } catch (error) {
        return { success: false, message: '公文書取得完了の通知に失敗しました' }
      } finally {
        this.loading = false
      }
    },

    // 公文書署名検証
    async verifyOfficialSignature(id) {
      try {
        this.loading = true
        // 指定されたIDの申請が存在しない場合
        if (!this.getApplicationById(id)) {
          throw new Error('Application not found')
        }

        const data = {
          id: id,
        }

        const response = await apiService.post('/api/egov/official-verify', data)

        // 署名検証結果をセット
        this.signatureVerificationResult = response.result || null

        return {
          success: true,
          data: this.signatureVerificationResult,
          message: response.message || '署名を検証しました',
        }
      } catch (error) {
        return { success: false, message: '署名検証に失敗しました' }
      } finally {
        this.loading = false
      }
    },

    // 公文書ダウンロード
    async downloadOfficialDocument(id, noticeSubId = null) {
      try {
        this.loading = true
        const application = this.getApplicationById(id)
        if (!application) {
          throw new Error('Application not found')
        }

        if (!application.officialList?.length) {
          return { success: false, message: '公文書が見つかりません' }
        }

        // noticeSubIdが指定されていない場合は最初の公文書を取得
        const targetNoticeSubId = noticeSubId || application.officialList[0].noticeSubId

        // 公文書取得API呼び出し
        const getResult = await this.getOfficialDocument(id, targetNoticeSubId)

        if (!getResult.success) {
          return getResult
        }

        // 取得完了API呼び出し
        const completeResult = await this.completeOfficialDocument(id, targetNoticeSubId)

        return {
          success: true,
          data: {
            document: getResult.data,
            completion: completeResult.data,
          },
          message: '公文書をダウンロードし、取得完了を通知しました',
        }
      } catch (error) {
        return { success: false, message: '公文書のダウンロードに失敗しました' }
      } finally {
        this.loading = false
      }
    },

    // 公文書一覧取得
    async getOfficialDocumentList(id) {
      try {
        this.loading = true
        const application = this.getApplicationById(id)
        if (!application) {
          return { success: false, message: '申請が見つかりません' }
        }

        if (!application.officialList?.length) {
          return { success: false, message: '公文書が見つかりません' }
        }

        // 公文書一覧を取得（実際のAPIから取得するか、既存のofficialListを使用）
        this.officialDocumentList = application.officialList.map(official => ({
          id: official.noticeSubId,
          noticeSubId: official.noticeSubId,
          title: official.docTitle || `公文書 ${official.noticeSubId}`,
          allowedDate: official.allowedDate,
          downloadExpiredDate: official.docDownloadExpiredDate,
          downloadDate: official.docDownloadDate,
          sign: official.sign,
          status: official.docDownloadDate ? '取得済み' : '未取得',
        }))

        return {
          success: true,
          data: this.officialDocumentList,
          message: '公文書一覧を取得しました',
        }
      } catch (error) {
        return { success: false, message: '公文書一覧の取得に失敗しました' }
      } finally {
        this.loading = false
      }
    },

    // 個別公文書ダウンロード
    async downloadSingleOfficialDocument(id, noticeSubId) {
      try {
        this.loading = true
        const application = this.getApplicationById(id)
        if (!application) {
          return { success: false, message: '申請が見つかりません' }
        }

        // 公文書取得API呼び出し
        const getResult = await this.getOfficialDocument(id, noticeSubId)

        if (!getResult.success) {
          return getResult
        }

        // 取得完了API呼び出し
        const completeResult = await this.completeOfficialDocument(id, noticeSubId)

        return {
          success: true,
          data: {
            document: getResult.data,
            completion: completeResult.data,
          },
          message: '公文書をダウンロードし、取得完了を通知しました',
        }
      } catch (error) {
        return { success: false, message: '公文書のダウンロードに失敗しました' }
      } finally {
        this.loading = false
      }
    },

    // e-Gov手続きリスト取得
    async getEGovProcList() {
      try {
        const response = await apiService.get('/api/egov/procedures')
        this.procList = response.procedures || []
        return this.procList
      } catch (error) {
        return []
      }
    },

    // ページング更新
    updatePaging(forward) {
      const newOffset = forward ? this.offset + this.limit : this.offset - this.limit
      this.offset = Math.max(0, newOffset)
    },

    // IDで申請を検索
    getApplicationById(id) {
      return this.applications.find(app => app.id === Number(id))
    },

    // 取り下げ用ZIP生成
    getEgovWithdrawZip(procId, arriveId, requesterName, cause, procInfo) {
      const procName = procInfo['e-Gov上での表示手続名（手続名称）']
      const now = new Date()
      const fileName = 'torisageirai.xml'

      // 外部ファイルから取下げ依頼XMLテンプレートを取得
      const torisageiraiXmlTempl = getTorisageiraiXmlTemplate()

      const withdrawXmlText = torisageiraiXmlTempl
        .replace('{{arrive_id}}', arriveId)
        .replace('{{proc_name}}', procName)
        .replace('{{requester_name}}', requesterName)
        .replace('{{year}}', now.getFullYear())
        .replace('{{month}}', now.getMonth() + 1)
        .replace('{{day}}', now.getDate())
        .replace('{{cause}}', cause)

      // kousei.xml生成
      const applyDataList = [
        {
          FormatId: '999000000000000003',
          Version: '0001',
          Name: '取下げ依頼XML',
          Filename: fileName,
        },
      ]
      const kouseiTxt = this.createWithdrawKouseiData(procInfo, arriveId, '', '', {}, {}, applyDataList, undefined)

      // ZIP作成
      return this.zipFiles(
        {
          [fileName]: withdrawXmlText,
          'kousei.xml': kouseiTxt,
        },
        procId
      )
    },

    // 構成データ作成
    createWithdrawKouseiData(procInfo, arriveId, destId, destName, applicantDic, contactDic, applyDataList, attachedFiles) {
      // 外部ファイルから構成情報XMLテンプレートを取得
      const kouseiXmlTempl = getKouseiXmlTemplate()

      const parser = new DOMParser()
      const kouseiObj = parser.parseFromString(kouseiXmlTempl, 'application/xml')

      if (!kouseiObj) {
        return undefined
      }

      const updateList = [
        ['様式ID', '999000000000000009'],
        ['様式バージョン', '0001'],
        ['STYLESHEET', '999000000000000009.xsl'],
        ['受付行政機関ID', ''],
        ['手続ID', '9990000000000003'],
        ['手続名称', procInfo['e-Gov上での表示手続名（手続名称）']],
        ['初回受付番号', arriveId],
        ['申請種別', '取下げ依頼'],
        ['提出先識別子', destId],
        ['提出先名称', destName],
        ['申請書様式ID', '999000000000000003'],
        ['申請書様式バージョン', '0001'],
        ['申請書様式名称', '取下げ依頼XML'],
        ['申請書ファイル名称', 'torisageirai.xml'],
      ]

      this.updateXmlData(kouseiObj, updateList)

      const serializer = new XMLSerializer()
      return serializer.serializeToString(kouseiObj)
    },

    // XMLデータ更新
    updateXmlData(xmlObject, updateList) {
      for (const data of updateList) {
        const [key, value] = data
        const node = xmlObject.querySelector(key)
        if (!node) {
          return undefined
        } else {
          node.textContent = value
        }
      }
    },

    // ZIP作成
    zipFiles(fileDataDic, procId) {
      const folder = {}
      for (const [name, txt] of Object.entries(fileDataDic)) {
        folder[name] = strToU8(txt)
      }
      const zipped = zipSync({ [procId]: folder })
      return zipped
    },

    // 日付フォーマット
    datetimeFormat(value) {
      return value ? value.replace(/-/g, '/').replace('T', ' ') : ''
    },
  },

  getters: {
    // 現在のページ数
    currentPage: state => {
      return Math.ceil(state.offset / state.limit) + 1
    },

    // 総ページ数
    totalPages: state => {
      return Math.ceil(state.totalCount / state.limit)
    },

    // 申請が取り下げ可能かどうかを判定（メソッドからゲッターに変更）
    canWithdrawApplication: state => {
      if (!state.formData) return false

      // 取り下げ依頼中、手続終了の文字がステータスに含まれる場合は取り下げ依頼できない
      return !(state.formData.status.includes('手続終了') || state.formData.status.includes('取下げ'))
    },

    // 取り下げボタンを表示するかどうかを判定
    shouldShowWithdrawButton: state => {
      if (!state.formData) return false

      // ステータスが空、またはステータスに「取下げ」の文字が含まれている場合は非表示
      return state.formData.id && !state.formData.status.includes('取下げ')
    },

    // 公文書が存在するかどうかを判定
    hasOfficialDocuments: state => {
      if (!state.formData) return false
      return state.formData.docCount > 0
    },

    // 前のページに戻れるかどうかを判定
    canGoPrev: state => {
      return state.offset > 0
    },

    // 次のページに進めるかどうかを判定
    canGoNext: state => {
      return state.offset + state.limit < state.totalCount
    },
  },
})
